/* eslint-disable max-len */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectDashboardOrgKey } from '@redux';
import { useOrgTreeContext } from '@contexts/org-tree-context';
import { extractOrgIdFromOrgKey, injectContractorViewMode } from '@legacy-modules/dashboard/utils/OrgKeyUtils';
import { ContractorViewMode } from '@legacy-modules/navigation/constants/ContractorViewMode';
import { fetchPaketshopVisits, reprocessPaketshopVisits } from '../services/PaketshopVisitService';
import { IPaketshopReprocessSummary, IPaketshopVisit, PaketshopVisitStatus } from '../interfaces/IPaketshopVisit';
import styles from './PaketshopVisitsPage.module.scss';

function getTodayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function getYesterdayIso(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
}

function formatArrival(isoString?: string): string {
  if (!isoString) {
    return '--';
  }
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) {
      return '--';
    }
    const hours = String(d.getUTCHours()).padStart(2, '0');
    const minutes = String(d.getUTCMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  } catch {
    return '--';
  }
}

function formatDwellTime(seconds?: number): string {
  if (!seconds || seconds <= 0) {
    return '--';
  }
  const mins = Math.round(seconds / 60);
  if (mins === 0) {
    return `${seconds} s`;
  }
  return `${mins} min`;
}

function formatTours(tours?: string[]): string {
  if (!tours || tours.length === 0) {
    return '--';
  }
  return `Tour ${tours.join(', ')}`;
}

/**
 * Returns visible page indices (0-based) for pagination buttons, using -1 to represent ellipsis.
 */
function getVisiblePageNumbers(currentPage: number, totalPages: number): number[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i);
  }
  const pages: Set<number> = new Set();
  pages.add(0);
  pages.add(totalPages - 1);

  for (let i = Math.max(0, currentPage - 2); i <= Math.min(totalPages - 1, currentPage + 2); i++) {
    pages.add(i);
  }
  if (currentPage <= 2) {
    pages.add(1);
    pages.add(2);
    pages.add(3);
  }
  if (currentPage >= totalPages - 3) {
    pages.add(totalPages - 4);
    pages.add(totalPages - 3);
    pages.add(totalPages - 2);
  }

  const sorted = Array.from(pages).sort((a, b) => a - b);
  const result: number[] = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) {
      result.push(-1); // Ellipsis indicator
    }
    result.push(sorted[i]);
  }
  return result;
}

export const PaketshopVisitsPage: React.FC = () => {
  const [date, setDate] = useState<string>(getTodayIso());
  const [visits, setVisits] = useState<IPaketshopVisit[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Context & Redux org selection
  const orgTreeContext = useOrgTreeContext();
  const reduxOrgKey = useSelector(selectDashboardOrgKey);
  const orgKey = orgTreeContext?.orgKey || reduxOrgKey;
  const orgTree = orgTreeContext?.orgTree;

  // Resolve selected organization node from OrgTree
  const selectedNode = useMemo(() => {
    if (!orgTree || !orgKey) return null;
    return orgTree.findOrgUnitValueByFunction(
      (n) =>
        n.orgUnit?.orgKey === orgKey || n.orgUnit?.orgKey === injectContractorViewMode(orgKey, ContractorViewMode.All)
    );
  }, [orgTree, orgKey]);

  const selectedOrgId = useMemo(() => {
    let curr = selectedNode;
    while (curr) {
      if (curr.orgUnit?.name) {
        // 1. Check for number in parentheses: e.g. "ZSB Bad Oldesloe (2360)" -> "2360"
        const matchParen = /\((\d+)\)/.exec(curr.orgUnit.name);
        if (matchParen) return matchParen[1];

        // 2. Check for "Depot <number>": e.g. "Depot 10 - Leipzig" -> "10"
        const matchDepot = /Depot\s+(\d+)/i.exec(curr.orgUnit.name);
        if (matchDepot) return matchDepot[1];
      }
      if (curr.orgUnit?.properties?.orgId) {
        return String(curr.orgUnit.properties.orgId);
      }
      curr = curr.parent;
    }
    return extractOrgIdFromOrgKey(orgKey);
  }, [selectedNode, orgKey]);

  // Find parent depot if selected node is a child (e.g. ZSB, contractor, cluster)
  const parentDepotNode = useMemo(() => {
    if (!selectedNode) return null;
    let curr = selectedNode.parent;
    while (curr) {
      if (curr.orgUnit?.orgType === 'hermes_org_depot' || curr.orgUnit?.orgKey?.startsWith('od:')) {
        return curr;
      }
      curr = curr.parent;
    }
    return null;
  }, [selectedNode]);

  const selectedOrgName =
    selectedNode?.orgUnit?.name || (selectedOrgId ? `Depot ${selectedOrgId}` : 'Depot 10 - Leipzig');
  const parentDepotName = parentDepotNode?.orgUnit?.name || 'Depot Overview';

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [search, setSearch] = useState<string>('');

  // Pagination state (default: 25 items per page)
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(25);

  // Reset pagination to first page when date, org, status or search changes
  useEffect(() => {
    setPageIndex(0);
  }, [date, selectedOrgId, statusFilter, search]);

  // Reprocess state
  const [reprocessing, setReprocessing] = useState<boolean>(false);
  const [reprocessSummary, setReprocessSummary] = useState<IPaketshopReprocessSummary | null>(null);

  // Load real staging data for selected date and org
  const loadVisits = useCallback(async (targetDate: string, orgId?: string | null) => {
    setLoading(true);
    setError(null);
    try {
      let data: IPaketshopVisit[] = [];
      if (orgId && orgId !== 'ALL') {
        data = await fetchPaketshopVisits(targetDate, orgId);
      }
      // If direct depot query returned no data (or nationwide date query),
      // fetch all facts for date and filter on client side
      if (!data || data.length === 0) {
        const allData = await fetchPaketshopVisits(targetDate);
        if (orgId && orgId !== 'ALL') {
          const matched = (allData || []).filter(
            (v) =>
              v.depotId === orgId ||
              v.zsbNummer === orgId ||
              v.clusterId === orgId ||
              (v.clusterName && v.clusterName.includes(orgId))
          );
          data = matched;
        } else {
          data = allData || [];
        }
      }
      setVisits(data || []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg || 'Fehler beim Laden der Paketshop-Besuchsdaten.');
      setVisits([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadVisits(date, selectedOrgId);
  }, [date, selectedOrgId, loadVisits]);

  // Overall KPI counts from real staging data
  const kpis = useMemo(() => {
    let serviced = 0;
    let closed = 0;
    let unvisited = 0;

    visits.forEach((v) => {
      const st = v.visitStatus || v.visitStatusLabel || (v.isServiced ? 'SERVICED' : 'KEINE_ANFAHRT');
      if (st === 'SERVICED') {
        serviced++;
      } else if (st === 'GESCHLOSSEN') {
        closed++;
      } else {
        unvisited++;
      }
    });

    const total = visits.length;
    const rate = total > 0 ? ((serviced / total) * 100).toFixed(1) : '0.0';

    return { total, serviced, closed, unvisited, rate };
  }, [visits]);

  // Filtered rows for display
  const filteredVisits = useMemo(() => {
    const q = search.trim().toLowerCase();

    return visits.filter((v) => {
      const st = v.visitStatus || v.visitStatusLabel || (v.isServiced ? 'SERVICED' : 'KEINE_ANFAHRT');

      // Status filter
      if (statusFilter !== 'ALL' && st !== statusFilter) {
        return false;
      }

      // Search text
      if (q) {
        const matchesShopNr = String(v.paketshopNummer || v.paketshopId || '')
          .toLowerCase()
          .includes(q);
        const matchesName = v.paketshopName?.toLowerCase().includes(q);
        const matchesTour = v.servicingTours?.some((t) => String(t).toLowerCase().includes(q));

        const matchesAny = matchesShopNr || matchesName || matchesTour;
        if (!matchesAny) {
          return false;
        }
      }

      return true;
    });
  }, [visits, statusFilter, search]);

  // Page calculations for TablePagination
  const pageCount = Math.max(1, Math.ceil(filteredVisits.length / pageSize));

  // Guard against pageIndex out-of-bounds when result list shrinks
  useEffect(() => {
    if (pageIndex >= pageCount) {
      setPageIndex(Math.max(0, pageCount - 1));
    }
  }, [pageCount, pageIndex]);

  const paginatedVisits = useMemo(() => {
    const start = pageIndex * pageSize;
    return filteredVisits.slice(start, start + pageSize);
  }, [filteredVisits, pageIndex, pageSize]);

  const visiblePages = useMemo(() => getVisiblePageNumbers(pageIndex, pageCount), [pageIndex, pageCount]);

  // Trigger reprocess / replay pipeline against staging Cassandra
  const handleReprocess = async () => {
    setReprocessing(true);
    setReprocessSummary(null);
    setError(null);
    try {
      const summary = await reprocessPaketshopVisits(date);
      setReprocessSummary(summary);
      await loadVisits(date, selectedOrgId);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg || 'Fehler beim Neu-Berechnen der Besuchsdaten.');
      await loadVisits(date, selectedOrgId);
    } finally {
      setReprocessing(false);
    }
  };

  // Export filtered rows to CSV
  const handleExportCsv = () => {
    if (!filteredVisits.length) return;

    const headers = ['Paketshop ID & Name', 'Status', 'Visits', 'Arrival', 'Dwell Time', 'Tours', 'Parcels'];

    const rows = filteredVisits.map((v) => [
      `"${v.paketshopNummer || v.paketshopId} – ${v.paketshopName || ''}"`,
      `"${v.visitStatus || v.visitStatusLabel || (v.isServiced ? 'SERVICED' : 'KEINE_ANFAHRT')}"`,
      v.visitCount || 0,
      `"${formatArrival(v.firstVisitStartTime)}"`,
      `"${formatDwellTime(v.totalDwellTimeSeconds)}"`,
      `"${formatTours(v.servicingTours)}"`,
      v.totalOrdersHandled || 0,
    ]);

    const csvContent = '\uFEFF' + [headers.join(';'), ...rows.map((r) => r.join(';'))].join('\r\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `Paketshop_Visits_${date}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderStatusBadge = (status?: PaketshopVisitStatus | string) => {
    switch (status) {
      case 'SERVICED':
        return <span className={styles.badgeServiced}>SERVICED</span>;
      case 'GESCHLOSSEN':
        return <span className={styles.badgeClosed}>GESCHLOSSEN</span>;
      case 'KEINE_ANFAHRT':
      default:
        return <span className={styles.badgeSkipped}>NO JOURNEY</span>;
    }
  };

  return (
    <div className={styles.page}>
      {/* Top Breadcrumb Navigation Header */}
      <div className={styles.topNav}>
        <div className={styles.breadcrumb}>
          <span>LMA</span>
          <span>&gt;</span>
          <span>{parentDepotName}</span>
          <span>&gt;</span>
          <span className={styles.activeBreadcrumb}>{selectedOrgName}</span>
        </div>
      </div>

      {/* Controls & Action Header */}
      <div className={styles.headerControls}>
        <div className={styles.datePickerGroup}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: '#475569' }}>Datum:</span>
          <input
            className={styles.dateInput}
            type='date'
            value={date}
            max={getTodayIso()}
            onChange={(e) => setDate(e.target.value || getTodayIso())}
          />
          <button type='button' className={styles.quickDateBtn} onClick={() => setDate(getTodayIso())}>
            Heute
          </button>
          <button type='button' className={styles.quickDateBtn} onClick={() => setDate(getYesterdayIso())}>
            Gestern
          </button>
        </div>

        <div className={styles.actionButtons}>
          <button
            type='button'
            className={styles.btnSecondary}
            onClick={() => loadVisits(date, selectedOrgId)}
            disabled={loading || reprocessing}
            title='Daten neu laden'>
            Aktualisieren
          </button>
          <button
            type='button'
            className={styles.btnSecondary}
            onClick={handleExportCsv}
            disabled={filteredVisits.length === 0}
            title='Als CSV-Datei exportieren'>
            CSV Export
          </button>
          <button
            type='button'
            className={styles.btnPrimary}
            onClick={handleReprocess}
            disabled={reprocessing || loading}
            title='Tagesaggregation erneut aus Rohdaten in Cassandra berechnen (Replay)'>
            {reprocessing ? 'Berechne...' : 'Neu berechnen (Replay)'}
          </button>
        </div>
      </div>

      {/* 4 Executive KPI Cards */}
      <div className={styles.kpiGrid}>
        <div className={styles.card}>
          <div className={styles.cardHeader}>TOTAL SHOPS: {kpis.total}</div>
          <div className={styles.cardSubtext}>Visit Rate: {kpis.rate}%</div>
        </div>

        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <span>SERVICED: {kpis.serviced}</span>
            <span className={styles.cardBadgeDotGreen} />
          </div>
          <div className={styles.cardSubtext}>(Handover Done)</div>
        </div>

        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <span>CLOSED: {kpis.closed}</span>
            <span className={styles.cardBadgeDotRed} />
          </div>
          <div className={styles.cardSubtext}>(Door Locked / Geschlossen)</div>
        </div>

        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <span>NOT VISITED: {kpis.unvisited}</span>
            <span className={styles.cardBadgeDotGrey} />
          </div>
          <div className={styles.cardSubtext}>(Keine Anfahrt / Skipped)</div>
        </div>
      </div>

      {/* Alerts */}
      {reprocessSummary && (
        <div className={styles.alertSuccess}>
          <span>
            <strong>Replay erfolgreich abgeschlossen ({reprocessSummary.durationMs} ms):</strong>{' '}
            {reprocessSummary.totalCandidates} Kandidaten ausgewertet &rarr; {reprocessSummary.servicedCount} Bedient,{' '}
            {reprocessSummary.closedCount} Geschlossen, {reprocessSummary.unvisitedCount} Nicht angefahren. Gesamt{' '}
            {reprocessSummary.persistedFactsCount} Fakten in Cassandra aktualisiert.
          </span>
          <button type='button' className={styles.btnSecondary} onClick={() => setReprocessSummary(null)}>
            Ausblenden
          </button>
        </div>
      )}

      {error && <div className={styles.alertError}>{error}</div>}

      {/* Filter Row: Pills on Left, Search on Right */}
      <div className={styles.filterRow}>
        <div className={styles.filterChips}>
          <button
            type='button'
            className={statusFilter === 'ALL' ? styles.pillActive : styles.pill}
            onClick={() => setStatusFilter('ALL')}>
            All ({kpis.total})
          </button>
          <button
            type='button'
            className={statusFilter === 'SERVICED' ? styles.pillActive : styles.pill}
            onClick={() => setStatusFilter('SERVICED')}>
            Serviced ({kpis.serviced})
          </button>
          <button
            type='button'
            className={statusFilter === 'GESCHLOSSEN' ? styles.pillActive : styles.pill}
            onClick={() => setStatusFilter('GESCHLOSSEN')}>
            Closed ({kpis.closed})
          </button>
          <button
            type='button'
            className={statusFilter === 'KEINE_ANFAHRT' ? styles.pillActive : styles.pill}
            onClick={() => setStatusFilter('KEINE_ANFAHRT')}>
            Skipped ({kpis.unvisited})
          </button>
        </div>

        <div className={styles.searchBox}>
          <span className={styles.searchIcon}>🔍</span>
          <input
            className={styles.searchInput}
            type='search'
            placeholder='Search Paketshop...'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Content & Table Area */}
      {loading && <div className={styles.emptyNote}>Paketshop-Besuchsdaten werden geladen...</div>}

      {!loading && visits.length === 0 && !error && (
        <div className={styles.emptyNote}>
          Keine Paketshop-Besuchsdaten für den {date} in Cassandra vorhanden. Klicken Sie auf &quot;Neu berechnen
          (Replay)&quot;, um die Aggregation aus Live-Rohdaten auszuführen.
        </div>
      )}

      {!loading && visits.length > 0 && filteredVisits.length === 0 && (
        <div className={styles.emptyNote}>Keine Paketshops entsprechen den ausgewählten Filterkriterien.</div>
      )}

      {!loading && filteredVisits.length > 0 && (
        <div className={styles.tableContainer}>
          <div className={styles.tableScrollArea}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Paketshop ID & Name</th>
                  <th>Status</th>
                  <th>Visits</th>
                  <th>Arrival</th>
                  <th>Dwell Time</th>
                  <th>Tours</th>
                  <th>Parcels</th>
                </tr>
              </thead>
              <tbody>
                {paginatedVisits.map((v) => (
                  <tr key={`${v.depotId}_${v.tourDatum}_${v.paketshopId}_${v.paketshopNummer}`}>
                    <td className={styles.colShop}>
                      {v.paketshopNummer || v.paketshopId} – {v.paketshopName || 'Paketshop'}
                    </td>
                    <td>
                      {renderStatusBadge(
                        v.visitStatus || v.visitStatusLabel || (v.isServiced ? 'SERVICED' : 'KEINE_ANFAHRT')
                      )}
                    </td>
                    <td>{v.visitCount ?? 0}</td>
                    <td>{formatArrival(v.firstVisitStartTime)}</td>
                    <td>{formatDwellTime(v.totalDwellTimeSeconds)}</td>
                    <td>{formatTours(v.servicingTours)}</td>
                    <td>{v.totalOrdersHandled ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className={styles.paginationBar}>
            <div className={styles.paginationInfo}>
              Zeige{' '}
              <strong>
                {filteredVisits.length === 0 ? 0 : pageIndex * pageSize + 1}–
                {Math.min((pageIndex + 1) * pageSize, filteredVisits.length)}
              </strong>{' '}
              von <strong>{filteredVisits.length}</strong> Paketshops
            </div>

            <div className={styles.paginationControls}>
              <button
                type='button'
                className={styles.pageBtn}
                disabled={pageIndex === 0}
                onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
                title='Vorherige Seite'>
                &lsaquo; Zurück
              </button>

              {visiblePages.map((page, idx) =>
                page === -1 ? (
                  <span key={`ellipsis-${idx}`} className={styles.pageEllipsis}>
                    ...
                  </span>
                ) : (
                  <button
                    key={`page-${page}`}
                    type='button'
                    className={pageIndex === page ? `${styles.pageBtn} ${styles.pageBtnActive}` : styles.pageBtn}
                    onClick={() => setPageIndex(page)}>
                    {page + 1}
                  </button>
                )
              )}

              <button
                type='button'
                className={styles.pageBtn}
                disabled={pageIndex >= pageCount - 1}
                onClick={() => setPageIndex((p) => Math.min(pageCount - 1, p + 1))}
                title='Nächste Seite'>
                Weiter &rsaquo;
              </button>
            </div>

            <div className={styles.pageSizeSelector}>
              <span>Einträge pro Seite:</span>
              <select
                className={styles.pageSizeSelect}
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPageIndex(0);
                }}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaketshopVisitsPage;
